import {
  AppBar,
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatBranchLabel } from '@/lib/branches';
import { formatDriveToImageSrc } from '@/lib/drive';
import { getGlassStyle } from '@/theme';
import { useThemeMode } from '@/store/ThemeContext';
import { useAuth } from '@/store/AuthContext';
import { SIDEBAR_WIDTH, SIDEBAR_MINI } from '../Sidebar';
import { navItems } from '../Sidebar/navItems';

interface TopBarProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onMobileMenuOpen: () => void;
}

export function TopBar({ sidebarOpen, onToggleSidebar, onMobileMenuOpen }: TopBarProps) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { preset, presets, setPreset } = useThemeMode();
  const { user, logout, selectedBranch, branchList, isSuperAdmin, setSelectedBranch } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [presetAnchorEl, setPresetAnchorEl] = useState<null | HTMLElement>(null);
  const [branchAnchorEl, setBranchAnchorEl] = useState<null | HTMLElement>(null);

  const sidebarW = sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_MINI;
  const currentItem = navItems.find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`));
  const currentPreset = presets.find((item) => item.value === preset);
  const selectedBranchSlug = (selectedBranch || user?.branch || '').toLowerCase();
  const activeBranch = branchList.find((b) => (b.slug || '').toLowerCase() === selectedBranchSlug);
  const branchLabel = activeBranch?.display_name || formatBranchLabel(selectedBranch || user?.branch || '');
  const branchHelper = isSuperAdmin
    ? `${branchList.length} accessible branches`
    : 'Current workspace branch';

  const initials = user?.name
    ? user.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : 'LM';

  const controlButtonSx = {
    width: 46,
    height: 46,
    borderRadius: 3.5,
    color: 'text.primary',
    border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.42 : 0.84),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
    },
  };

  const menuPaperSx = {
    mt: 1.25,
    minWidth: 244,
    borderRadius: 4,
    overflow: 'hidden',
    ...getGlassStyle(theme),
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.88 : 0.94),
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: 12,
        left: { xs: 12, md: sidebarW + 16 },
        width: { xs: 'calc(100% - 24px)', md: `calc(100% - ${sidebarW + 32}px)` },
        transition: 'left 0.2s ease, width 0.2s ease',
        zIndex: (t) => t.zIndex.drawer - 1,
        overflow: 'hidden',
        borderRadius: 4.5,
        backgroundImage: 'none',
        color: 'text.primary',
        ...getGlassStyle(theme),
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.72 : 0.8),
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at top left, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14)} 0%, transparent 42%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Toolbar
        sx={{
          position: 'relative',
          zIndex: 1,
          gap: 1.25,
          // Slim bar on phones (52 px) → full height on tablets/desktop (72 px).
          // Keeps precious vertical real estate free for content on small screens.
          minHeight: { xs: '52px !important', md: `${theme.custom.layout.topbarHeight - 8}px !important` },
          px: { xs: 1.25, sm: 1.75, lg: 2.25 },
        }}
      >
        <IconButton onClick={onMobileMenuOpen} sx={{ display: { md: 'none' }, ...controlButtonSx }}>
          <MenuIcon />
        </IconButton>

        <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
          <IconButton onClick={onToggleSidebar} sx={{ display: { xs: 'none', md: 'flex' }, ...controlButtonSx }}>
            {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        </Tooltip>

        <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: { xs: 1, xl: 1.5 } }}>
          <Box sx={{ minWidth: 0, flexShrink: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', letterSpacing: '0.08em', textTransform: 'uppercase' }} noWrap>
              {currentItem?.group ?? 'Workspace'}
            </Typography>
            <Typography variant="h6" fontWeight={700} noWrap>
              {currentItem?.label ?? 'Dashboard'}
            </Typography>
          </Box>

          <Box
            sx={{
              display: { xs: 'none', xl: 'flex' },
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              px: 1.25,
              py: 0.9,
              borderRadius: 3.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
              backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.34 : 0.74),
            }}
          >
            <Chip size="small" label={currentPreset?.label ?? 'Theme'} sx={{ height: 22, borderRadius: 2 }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              Premium admin workspace
            </Typography>
          </Box>
        </Box>

        {/* Branch selector — icon-only on sm, full label on md+, hidden on xs */}
        <Tooltip title={isSuperAdmin ? 'Switch active branch' : 'Current branch'}>
          <ButtonBase
            onClick={isSuperAdmin ? (event) => setBranchAnchorEl(event.currentTarget) : undefined}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              minWidth: { sm: 46, md: 238 },
              maxWidth: { sm: 46, md: 280 },
              justifyContent: 'space-between',
              gap: 1,
              px: { xs: 0, sm: 1.4 },
              py: { xs: 0, sm: 1 },
              height: 46,
              borderRadius: 3.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.42 : 0.84),
              cursor: isSuperAdmin ? 'pointer' : 'default',
            }}
          >
            <Box
              sx={{
                ml: { xs: 1.1, sm: 0 },
                width: 32,
                height: 32,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
              }}
            >
              <BusinessRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }} noWrap>
                {isSuperAdmin ? 'Active branch' : 'Workspace'}
              </Typography>
              <Typography variant="body2" fontWeight={700} noWrap>
                {branchLabel}
              </Typography>
            </Box>
            {isSuperAdmin && <KeyboardArrowDownRoundedIcon sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 18, color: 'text.secondary' }} />}
          </ButtonBase>
        </Tooltip>

        {/* Utility buttons — hidden on xs to prevent TopBar crowding on 360 px phones */}
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}>
          <Tooltip title="Notifications">
            <IconButton onClick={() => navigate('/notifications')} sx={controlButtonSx}>
              <NotificationsNoneRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Change color preset">
            <IconButton onClick={(e) => setPresetAnchorEl(e.currentTarget)} sx={controlButtonSx}>
              <PaletteOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Tooltip title="Account">
          <ButtonBase
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              gap: 1,
              pl: { xs: 0.5, lg: 1.1 },
              pr: 0.5,
              py: 0.45,
              borderRadius: 3.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
              backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.42 : 0.86),
            }}
          >
            <Box sx={{ display: { xs: 'none', lg: 'block' }, minWidth: 0, textAlign: 'right' }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {user?.name ?? 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.role ?? 'Administrator'}
              </Typography>
            </Box>
            <Avatar
              src={formatDriveToImageSrc(user?.avatar_url)}
              sx={{
                width: 38,
                height: 38,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 700,
                boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.28)}`,
              }}
            >
              {!user?.avatar_url && initials}
            </Avatar>
          </ButtonBase>
        </Tooltip>

        <Menu
          anchorEl={branchAnchorEl}
          open={Boolean(branchAnchorEl)}
          onClose={() => setBranchAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { sx: menuPaperSx } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" fontWeight={700}>Switch branch</Typography>
            <Typography variant="caption" color="text.secondary">{branchHelper}</Typography>
          </Box>
          <Divider />
          {branchList.map((branch) => {
            const slug = (branch.slug || '').toLowerCase();
            const isActive = slug === (selectedBranch || '').toLowerCase();
            const isHomeBranch = slug === (user?.branch || '').toLowerCase();

            return (
              <MenuItem
                key={branch.id}
                selected={isActive}
                onClick={() => {
                  setSelectedBranch(branch.slug);
                  setBranchAnchorEl(null);
                }}
              >
                {isActive ? <CheckIcon fontSize="small" style={{ marginRight: 12 }} /> : <Box sx={{ width: 24, mr: 1.5 }} />}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={isActive ? 700 : 500} noWrap>
                    {branch.display_name || formatBranchLabel(branch.slug)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {isHomeBranch ? 'Home branch access' : 'Shared admin branch view'}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Menu>

        <Menu
          anchorEl={presetAnchorEl}
          open={Boolean(presetAnchorEl)}
          onClose={() => setPresetAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { sx: menuPaperSx } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" fontWeight={700}>Theme preset</Typography>
            <Typography variant="caption" color="text.secondary">Switch accent colors across the app.</Typography>
          </Box>
          <Divider />
          {presets.map((item) => (
            <MenuItem
              key={item.value}
              selected={item.value === preset}
              onClick={() => {
                setPreset(item.value);
                setPresetAnchorEl(null);
              }}
            >
              {item.value === preset ? <CheckIcon fontSize="small" style={{ marginRight: 12 }} /> : <Box sx={{ width: 24, mr: 1.5 }} />}
              {item.label}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { sx: menuPaperSx } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" fontWeight={700}>{user?.name ?? 'User'}</Typography>
            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
            <Box sx={{ mt: 1 }}>
              <Chip size="small" label={user?.role ?? 'Administrator'} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main', fontWeight: 600 }} />
            </Box>
          </Box>
          <Divider />
          <MenuItem onClick={() => { navigate('/profile'); setAnchorEl(null); }}>
            <PersonIcon fontSize="small" sx={{ mr: 1.5 }} /> Profile
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => { logout(); navigate('/login'); setAnchorEl(null); }}
            sx={{ color: 'error.main' }}
          >
            <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} /> Sign Out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

